
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { generateVerificationCode, sendLoginCodeEmail, verifyCode } from "@/services/authService";
import { clearSessionData, clearUserData, getSessionData, storeSessionData, storeUserData } from "@/hooks/use-auth-session";
import { findOrCreateProfile } from "@/services/userProfileService";

export const useAuthOperations = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  // Send login code via email
  const sendLoginCode = async (email: string, fullName: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      
      // Generate a random 4-digit code
      const code = generateVerificationCode();
      
      console.log("Sending login code to:", email, "Code:", code);
      
      // Send the email with the code
      await sendLoginCodeEmail(email, fullName, code);

      // Store the session data temporarily
      storeSessionData(email, fullName);
      
      toast({
        title: "Verification Code Sent",
        description: "Please check your email for the 4-digit verification code."
      });
      
      return true;
    } catch (error) {
      console.error("Error sending login code:", error);
      toast({
        title: "Failed to Send Code",
        description: "There was an error sending the verification code. Please try again.",
        variant: "destructive"
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Verify the login code
  const verifyLoginCode = async (email: string, code: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      
      const sessionData = getSessionData();
      if (!sessionData) {
        toast({
          title: "Session Expired",
          description: "Your login session has expired. Please start over.",
          variant: "destructive"
        });
        return false;
      }

      // Use the email from session data to ensure consistency
      const sessionEmail = sessionData.email;
      const { fullName } = sessionData;
      
      console.log("Verifying code for:", sessionEmail, "Code:", code);

      // Verify the code
      const isValid = await verifyCode(sessionEmail, code);

      if (!isValid) {
        toast({
          title: "Invalid Code",
          description: "The verification code is invalid or has expired.",
          variant: "destructive"
        });
        return false;
      }

      // Use the findOrCreateProfile function to ensure a profile exists
      try {
        await findOrCreateProfile(sessionEmail, fullName);
      } catch (profileError) {
        console.error("Error updating profile:", profileError);
        // Don't block login if profile creation fails
      }

      // Authentication successful - set user in localStorage
      const userData = { email: sessionEmail, fullName };
      storeUserData(userData);
      
      // Clean up
      clearSessionData();
      
      toast({
        title: "Login Successful",
        description: `Welcome back, ${fullName || 'user'}!`
      });
      
      return true;
    } catch (error) {
      console.error("Error verifying code:", error);
      toast({
        title: "Verification Failed",
        description: "There was an error verifying your code. Please try again.",
        variant: "destructive"
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Logout the user
  const logout = async (): Promise<void> => {
    clearUserData();
    toast({
      title: "Logged Out",
      description: "You have been successfully logged out."
    });
  };

  return {
    sendLoginCode,
    verifyLoginCode,
    logout,
    isLoading
  };
};
